<template>
  <div class="workflow-ingredients-view">
    <ButtonGroup class="workflow-view__actions">
      <Button
        v-if="productMenuVisible && !isFromEmailLink"
        theme-color="primary"
        type="button"
        @click="redirect(`${Pages.WorkflowOverview}?mode=my`)"
        data-cy="my-work-button"
        >My Work</Button
      >
      <Button
        v-if="productMenuVisible && !isFromEmailLink"
        theme-color="primary"
        type="button"
        @click="redirect(`${Pages.WorkflowOverview}?mode=all`)"
        data-cy="all-work-button"
        >All Work</Button
      >
      <Button
        v-if="ingredientMenuVisible && !isFromEmailLink"
        theme-color="primary"
        type="button"
        @click="
          redirect(
            `${Pages.IarSubmissions}?activeTab=${urlParams.get('activeTab')}`
          )
        "
        data-cy="back-button"
        >Back</Button
      >
    </ButtonGroup>

    <div v-if="isShowUrgent" class="urgent-container">
      This workflow item is marked as Urgent &nbsp;
      <img class="urgent-icon" :src="urgentIcon" alt="urgent" />
    </div>

    <UserActivityView />

    <div class="details-container">
      <div
        class="details"
        v-for="(subArr, i) in [
          headerDetails.slice(0, Math.ceil(headerDetails.length / 2)),
          headerDetails.slice(Math.ceil(headerDetails.length / 2)),
        ]"
        :key="i"
      >
        <div class="details_wrapper" v-for="sub in subArr" :key="sub.label">
          <span class="details__label">{{ sub.label }} &nbsp;</span>
          <span> {{ sub.value }}</span>
        </div>
      </div>
    </div>

    <div class="urgent-checkbox">
      <label for="isUrgent">URGENT</label>
      <input
        type="checkbox"
        id="isUrgent"
        v-model="isShowUrgent"
        @change="onUrgentClick"
      />
    </div>

    <div class="pull-right cc-label-name">
      <label for="showOriginalCheckbox"
        >Switch to Original Submissions View</label
      >
      <input
        type="checkbox"
        id="showOriginalCheckbox"
        v-model="isShowOriginal"
        @change="showOriginal"
      />
    </div>
    <div
      v-if="isIarPage || !isMobile()"
      class="ingredients-table"
      data-cy="ingredients-table"
    >
      <IngredientDetailsGrid
        :ingredientDetails="ingredientDetails"
        :loading="ingredientsLoading"
        :showOriginal="isShowOriginal"
        :statusOptions="submissionStatuses"
        :canEditIarLineItemStatus="canEditIarLineItemStatus"
        @statusChanged="onStatusChanged"
      />
    </div>
    <LineItemsView
      v-else
      :lineItems="ingredientDetails"
      :useOriginalView="isShowOriginal"
      :loadingLineItems="ingredientsLoading"
    />

    <div v-if="companyComment" class="comment-container">
      <i><b>Company Comment:</b>&nbsp; {{ companyComment }}</i>
    </div>

    <div class="two-box-container">
      <div class="box">
        <WorkflowSubmitForm
          v-if="activityHistory && ingredientDetails.length > 0"
          ref="workflowForm"
          :jobId="jobId"
          :workflowId="ingredientDetails[0]?.WorkflowId ?? 0"
          :status="activityHistory.Status!"
          :privateComment="activityHistory.LatestUserComment"
          :assignedTo="activityHistory!.AssignedTo"
          :wfSubmission="(activityHistory as WfSubmissionDisplay)"
          :wfSubmissionType="EnumWorkflowType.Ingredient"
          :companyId="ingredientDetails[0].CompanyID"
          :companyName="ingredientDetails[0].CompanyName"
          :attachments="workflowAttachments"
          :showApproveButton="approveButtonVisible"
          :lineItemsStatus="ingredientDetails[0].JobStatus"
          @formSubmitted="onFormSubmitted"
          @approveClicked="approveWorkflowAsync"
        />
      </div>

      <div class="box" data-cy="activity-history-table">
        <header class="activity-history-header">Activity History</header>
        <ActivityHistoryGrid
          :activity-history="activityHistory?.Actions ?? []"
          :loading="activityLoading"
          :jobId="jobId"
          :workflowId="ingredientDetails[0]?.WorkflowId ?? 0"
          :submissionType="EnumWorkflowType.Ingredient"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { UserRoles } from "@/Domain/UserRoles";
import { computed, onBeforeMount, ref, watchEffect } from "vue";
import urgentIcon from "@/assets/img/Urgent1Red.png";
import { useMainStore } from "@/stores/useMainStore";
import { Button, ButtonGroup } from "@progress/kendo-vue-buttons";
import ActivityHistoryGrid from "@/shared/ui/grids/ActivityHistoryGrid.vue";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { getJsonFilterString } from "@/shared/helpers/filtersHelper";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import IngredientDetailsGrid from "./WorkflowDetailIngredientsGrid.vue";
import type { SubmissionsTable } from "@/Domain/SubmissionsTable";
import type { SelectionRange } from "@progress/kendo-vue-dateinputs";
import type { WfIngredientSubmissionDisplay } from "@/Domain/WfIngredientSubmissionDisplay";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import type { GetSubmissionsWithFilteringRequest } from "@/api/submissionsOverview";
import type { IngredientSubmissionDetail } from "@/Domain/IngredientSubmissionDetail";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from "@progress/kendo-data-query";
import type { SharePointAttachment } from "@/Domain/SharePointAttachment";
import WorkflowSubmitForm from "@/shared/ui/forms/WorkflowSubmitForm.vue";
import { EnumWorkflowType } from "@/Domain/EnumWorkflowType";
import type { SubmissionStatus } from "@/Domain/SubmissionStatus";
import type { WfSubmissionDisplay } from "@/Domain/WfSubmissionDisplay";
import { Pages } from "@/pages/pages";
import UserActivityView from "@/shared/ui/activity/UserActivityView.vue";
import { useActivityStore } from "@/stores/useActivityStore";
import LineItemsView from "./LineItemsView.vue";
import { isMobile } from "@/shared/helpers/mobileHelper";

const main = useMainStore();
const notificationStore = useNotificationStore();
const activity = useActivityStore();

const workflowForm = ref<InstanceType<typeof WorkflowSubmitForm> | null>(null);
const urlParams = new URLSearchParams(window.location.search);
const jobId = urlParams.get("JobID") ?? "";
const checkOut = urlParams.get("CheckOut") || null;
activity.setActivityId("WorkflowDetailIngredients-" + jobId);

const submissionJobs = ref<SubmissionsTable[]>([]);
const submissionStatuses = ref<SubmissionStatus[]>([]);

const ingredientDetails = ref<IngredientSubmissionDetail[]>([]);

const activityHistory = ref<WfIngredientSubmissionDisplay>();
const submissionApproval = ref<boolean>(false);

const approvingWorkflow = ref<boolean>(false);
const activityLoading = ref<boolean>(false);
const ingredientsLoading = ref<boolean>(false);
const submissionsLoading = ref<boolean>(false);

const workflowAttachments = ref<SharePointAttachment[]>([]);

const isShowUrgent = ref<boolean>(false);
const isShowOriginal = ref<boolean>(false);

const companyComment = ref<string>("");

const skip = ref<number>(0);
const take = ref<number>(100);

const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);

const currentDate = new Date();
const startDate = new Date(
  currentDate.getFullYear() - 1,
  currentDate.getMonth(),
  currentDate.getDate()
);

const dateRange = ref({
  start: new Date(startDate),
  end: new Date(currentDate),
} as SelectionRange);

const filter = ref<CompositeFilterDescriptor>({
  logic: "and",
  filters: [{} as FilterDescriptor],
});

const valueFiltersConvertOptions = [
  {
    column: "DocumentDate",
    convert: (value: string) => {
      return new Date(value).toLocaleDateString();
    },
    convertBack: (value: string) => {
      return `${value} 00:00:00`;
    },
  },
] as ValueFieldConvertOption[];

const headerDetails = computed(() => {
  return [
    {
      label: "Job ID:",
      value: activityHistory.value?.JobID,
    },
    {
      label: "Company:",
      value: ingredientDetails.value[0]
        ? `${ingredientDetails.value[0].CompanyName} (${ingredientDetails.value[0].CompanyID}) - ${ingredientDetails.value[0].CompanyStatus}`
        : "",
    },
    {
      label: "Assigned To:",
      value: activityHistory.value?.AssignedTo,
    },
    {
      label: "Category:",
      value: jobTypeToStringValue(
        Math.max(...ingredientDetails.value.map((li) => li.Type))
      ),
    },
    {
      label: "Checked Out By:",
      value: ingredientDetails.value[0]?.CheckOutBy,
    },
    {
      label: "Submission Date:",
      value: ingredientDetails.value[0]?.DateSubmitted,
    },
    {
      label: "Submitted By:",
      value: ingredientDetails.value[0]?.SubmittedBy,
    },
    {
      label: "RC:",
      value: submissionJobs.value[0]?.RC,
    },
    {
      label: "Workflow Status:",
      value: activityHistory.value?.StatusAsString,
    },
    {
      label: "Line Item(s) Status:",
      value:  ingredientDetails.value[0]?.JobStatus,
    },
  ] as {
    label: string;
    value: string;
  }[];
});

const JobType = {
  DELETE: 0,
  UKD: 1, // user is copying ingredient from ukd search results
  SCHEDULEA: 2, // user is copying ingredient from schedule A
  FORM: 3, // user is entering new ingredient from form
  //UKD_STAR_LABEL_NAME: "LABEL NAME NOT AVAILABLE",
};

const jobTypeToStringValue = function (jobType: number) {
  switch (jobType) {
    case JobType.DELETE:
      return "Ingredient Delete";
    case JobType.UKD:
      return "Ingredients Copied From UKD";
    case JobType.FORM:
      return "Ingredients Add By Name/Source";
    case JobType.SCHEDULEA:
      return "Ingredients Copied from Schedule A";
    // case JobType.UKD_STAR_LABEL_NAME:
    //   return "Ingredients Add By UKD ID";
    default:
      return "";
  }
};

const isFromEmailLink = computed(() => {
  return urlParams.get("emailLink")?.toLowerCase().includes("y") ?? false;
});

const isIarPage = computed(() => {
  const param = urlParams.get("IARSubmissionsPage");
  return (
    (isFromEmailLink.value && main.isInRole(UserRoles.ROLE_INGREDIENT)) ||
    (param != null && param !== "")
  );
});

const canEditIarLineItemStatus = function (
  details: IngredientSubmissionDetail
) {
  return (
    isIarPage.value &&
    (isFromEmailLink.value ||
      submissionJobs.value[0]?.CheckedOutBy?.toLowerCase() ===
        main.userLogin.toLowerCase()) &&
    ((submissionStatuses.value.some((u) => u.fieldValue === details.Status) &&
      submissionJobs.value[0]?.CheckedOutBy != "") ||
      details.MissingOnSchedA == "Y" || 
      (!details.OnScheduleA && !details.Status.includes("Duplicate")))
  );
};

const ingredientMenuVisible = computed(() => {
  return isIarPage.value && main.isInRole(UserRoles.ROLE_INGREDIENT);
});

const productMenuVisible = computed(() => {
  return (
    !isIarPage.value &&
    !(urlParams.get("IARSubmissionsPage")?.toLowerCase() == "y" || false)
  );
});

const typeForm = 3;

const approveButtonVisible = computed(() => {
  const jobType = ingredientDetails.value.reduce(
    (acc, cur) => (cur.Type > acc ? cur.Type : acc),
    -1
  );
  return (
    !approvingWorkflow.value &&
    submissionApproval.value &&
    jobType == typeForm &&
    main.isInRole(UserRoles.ROLE_RABBINIC_COORDINATOR) &&
    !ingredientDetails.value[0].NewBusinessIARJob &&
    !isIarPage.value
  );
});

const getSubmissionsWithFilteringAsync = async function (
  valuesForFieldOnly: string = ""
) {
  const requestParams = {
    userLogin: "ALL", // need to set this param to "ALL" (instead of user's login) to see list of all jobs
    iarLogin: "", // need to set this param to blank (instead of user's login) to see list of all jobs
    companyLogin: "0",
    status: "ALL",
    allJobTypes: 1,
    showCheckedOutJobs: 1,
    beginDate: dateRange.value.start?.toLocaleDateString("en-US"),
    endDate: dateRange.value.end?.toLocaleDateString("en-US"),
    jobId: jobId || "",
    filters: getJsonFilterString(filter.value, valueFiltersConvertOptions),
    take: take.value,
    skip: skip.value,
    sortField: sortField.value,
    ascending: sortAscending.value,
    valuesForFieldOnly: valuesForFieldOnly,
  } as GetSubmissionsWithFilteringRequest;

  if (!valuesForFieldOnly)
    return api.get("GetSubmissionsWithFiltering", requestParams);

  requestParams.take = 1000;
  return CacheService.getAsync(createCacheKey(requestParams), () =>
    api.get("GetSubmissionsWithFiltering", requestParams)
  );
};

const getSubmissionsAsync = async function () {
  if (submissionsLoading.value) return;
  submissionsLoading.value = true;

  await handleResponsePromiseAsync(
    getSubmissionsWithFilteringAsync(),
    (data) => {
      submissionJobs.value = data.ListItems;
    },
    "WorkflowDetailIngredientsView.vue",
    "getSubmissionsAsync"
  );

  submissionsLoading.value = false;
};

const getSubmissionStatusesAsync = async function () {
  await handleResponsePromiseAsync(
    CacheService.getAsync(
      createCacheKey(
        { userLogin: main.userLogin },
        "GetSubmissionStatusValues"
      ),
      () => api.get("GetSubmissionStatusValues", {})
    ),
    (data) => {
      submissionStatuses.value = data;
    },
    "WorkflowDetailIngredientsView.vue",
    "getSubmissionStatusesAsync"
  );
};

const getIngredientSubmissionDetailsAsync = async function () {
  if (ingredientsLoading.value) return;
  ingredientsLoading.value = true;

  await handleResponsePromiseAsync(
    api.get("GetIngredientSubmissionDetails", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      jobId: jobId || "",
      includeWorkflowDetails: true,
    }),
    (data) => {
      ingredientDetails.value = data;
      companyComment.value = data[0].UserComment;
    },
    "WorkflowDetailIngredientsView.vue",
    "getIngredientSubmissionDetailsAsync"
  );

  ingredientsLoading.value = false;
};

const getSubmissionApprovalAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("IngredientSubmissionApprovalGet", {
      userLogin: main.userLogin,
      jobId: jobId || "",
    }),
    (data) => {
      submissionApproval.value = data;
    },
    "WorkflowDetailIngredientsView.vue",
    "getSubmissionApprovalAsync"
  );
};

const approveWorkflowAsync = async function () {
  if (approvingWorkflow.value) return;
  approvingWorkflow.value = true;

  const errorNotification = {
    message: `Error approving workflow ${ingredientDetails.value[0].WorkflowId}. Please try again`,
    type: NotificationTypeEnum.error,
    duration: 5000,
  };

  await handleResponsePromiseAsync(
    api.post("UpdateIngredientSubmissionApproval", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      jobId: parseInt(jobId),
    }),
    (data) => {
      if (data) {
        notificationStore.notify({
          message: `Ingredient Job ${ingredientDetails.value[0].JobId} has been reviewed/approved`,
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
      } else {
        notificationStore.notify(errorNotification);
      }
    },
    "WorkflowDetailIngredientsView.vue",
    "approveWorkflowAsync",
    () => {
      notificationStore.notify(errorNotification);
    }
  );

  // Refresh submission approval status
  await getSubmissionApprovalAsync();

  approvingWorkflow.value = false;
};

const getActivityHistoryAsync = async function () {
  if (activityLoading.value) return;
  activityLoading.value = true;

  await handleResponsePromiseAsync(
    api.get("VueGetWfIngredientSubmission", {
      id: Number(jobId) || 0,
    }),
    (data) => {
      activityHistory.value = data;
      isShowUrgent.value = data?.IsUrgent ?? false;

      // Sort actions by date
      activityHistory.value.Actions = activityHistory.value.Actions.sort(
        (a, b) => {
          return (
            new Date(b.Timestamp).getTime() - new Date(a.Timestamp).getTime()
          );
        }
      );
    },
    "WorkflowDetailIngredientsTable.vue",
    "getActivityHistoryAsync"
  );

  activityLoading.value = false;
};

const workflowCheckOutJobAsync = async function () {
  let workflowCheckOut = false;

  await handleResponsePromiseAsync(
    api.get("WorkflowCheckOutJob", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      jobId: jobId || "",
    }),
    (data) => {
      workflowCheckOut = data;
    },
    "WorkflowDetailIngredientsView.vue",
    "workflowCheckOutJobAsync"
  );

  return workflowCheckOut;
};

const getWorkflowAttachmentsAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("GetWorkflowAttachments", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      jobId: jobId || "",
      workflowType: "ingredient",
    }),
    (data) => {
      workflowAttachments.value = data;
    },
    "WorkflowDetailIngredientsTable.vue",
    "GetWorkflowAttachmentsAsync"
  );
};

const redirect = function (href: string) {
  window.location.href = href;
};

const onUrgentClick = function () {
  const errorNotification = {
    message: `Error marking Ingredient Job ${
      ingredientDetails.value[0].JobId
    } as ${isShowUrgent.value ? "urgent" : "not urgent"}. Please try again`,
    type: NotificationTypeEnum.error,
    duration: 5000,
  };

  handleResponsePromiseAsync(
    api.get("SetWorkflowUrgentStatus", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      workflowId: ingredientDetails.value[0].WorkflowId,
      isUrgent: isShowUrgent.value,
    }),
    (data) => {
      if (data) {
        notificationStore.notify({
          message: `Ingredient Job ${
            ingredientDetails.value[0].JobId
          } marked as ${isShowUrgent.value ? "urgent" : "not urgent"} `,
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
      } else {
        notificationStore.notify(errorNotification);
      }
    },
    "WorkflowDetailIngredientsTable.vue",
    "onUrgentClick",
    () => {
      notificationStore.notify(errorNotification);
    }
  );
};

const showOriginal = function () {
  getIngredientSubmissionDetailsAsync();
};

const onFormSubmitted = function () {
  getSubmissionsAsync();
  getIngredientSubmissionDetailsAsync();
  getActivityHistoryAsync();
  getWorkflowAttachmentsAsync();
};

const onStatusChanged = function () {
  getSubmissionsAsync();
  // #29510: Any Time a line item status is Updated from Dropdown Trigger saveWorkflowDetailsAsync()
  workflowForm.value?.saveWorkflowDetailsAsync();
};

watchEffect(() => {
  ingredientDetails.value.forEach((item) => {
    item.Attachments = workflowAttachments.value.filter(
      (a) => a.CartId == item.IngredientInCartID
    );
  });
});

onBeforeMount(async () => {
  if (checkOut === "true") {
    await workflowCheckOutJobAsync();
  }
  const promises = [
    getIngredientSubmissionDetailsAsync(),
    getActivityHistoryAsync(),
    getSubmissionsAsync(),
  ];
  await Promise.all(promises);
  await getWorkflowAttachmentsAsync();

  getSubmissionStatusesAsync();
  getSubmissionApprovalAsync();
});
</script>

<style lang="scss">
.workflow-ingredients-view {
  .k-input-value-text {
    white-space: break-spaces;
  }
}
</style>

<style scoped lang="scss">
.workflow-ingredients-view {
  margin: 8px;
  padding: 5px;
  width: auto;
  display: flex;
  flex-direction: column;
  gap: 10px; //32px; //
  font-family: museo-sans, helvetica, arial, sans-serif;
  font-size: 13px;
  justify-content: center;
  height: auto;

  @include media-breakpoint-up(md) {
    margin: 28px;
  }

  .activity-history-header {
    font-weight: bold;
    padding: 16px;
    font-size: 14px;
    background: #dedede;
  }
}

.details-container {
  display: block; //flex
  justify-content: space-between;

  padding: 10px;
  border-bottom: 1px solid #ddd;
  background-color: #fff;
  height: auto; //180px;
  font-size: 14px;
  column-count: 2;
  column-gap: 20px;

  .details {
    display: flex;
    gap: 5px;
    column-gap: 36px;
    font-weight: 200;
    font-size: smaller;
    flex-wrap: wrap;
    flex-direction: column;

    &__label {
      font-weight: bold;
      color: #999;
    }
  }
}

.urgent-checkbox {
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  color: #999;
  font-weight: 700;
  margin-bottom: 15px;
  display: flex;
}

.cc-label-name {
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  color: #999;
  font-weight: 700;
  text-align: justify right;
}

.urgent-container {
  background-color: #f8d7da;
  border-color: #f5c2c7;
  position: relative;
  padding: 1rem 1rem;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  border-radius: 4px;
}

.comment-container {
  display: flex;
  padding: 12px;
  border-bottom: 1px solid #ccc;
  border-radius: 6px;
  box-shadow: 0 0 35px rgb(0 0 0 / 10%);
  background-color: #f6f4bf;
}

.activity-history__container {
  background-color: #ffffff;
  width: 100%;
  padding: 0px;
}

.ingredients-table {
  .grid-control__container {
    display: flex;
    align-items: center;
    background: #dedede;
    color: #999;
    font-weight: 700;
    font-size: 13px;
  }

  .form-link {
    text-decoration: none;
  }
}

.pull-right {
  display: flex;
  gap: 8px;
  color: #999;
  font-weight: 700;
  text-align: end;
  margin-top: 15px;
}

.two-box-container {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex-wrap: wrap;
  width: 100%;
  margin-top: 25px;

  @include media-breakpoint-up(md) {
    gap: 36px;
  }
}

.box {
  min-width: 320px;
  display: block;
  box-sizing: border-box;
  flex: 1;

  &.nopadding-box {
    padding: 2px;
  }

  &.borderless-box {
    border: 0px;
  }
}

.k-grid-header {
  background-color: #dedede;
}
</style>
